/** Mock data shaped exactly like the eventual API contract. */

export type Runner = {
  handle: string;
  displayName: string;
  city: string;
  country: string;
  countryFlag: string;
  ageBracket: string;
  avatar: string;
  form: "rising" | "steady" | "fading";
  formDelta: number;
  pbs: { distance: string; timeSeconds: number; setAt: string }[];
};

export type Prediction = {
  raceSlug: string;
  raceName: string;
  distanceLabel: string;
  p10Seconds: number;
  p50Seconds: number;
  p90Seconds: number;
  boomProbabilityPct: number;
  drivers: { label: string; deltaSeconds: number }[];
};

export type RaceEvent = {
  slug: string;
  name: string;
  city: string;
  country: string;
  date: string;
  distanceLabel: string;
  elevationM: number;
  terrain: "road" | "trail" | "track";
  friendsGoing: { handle: string; avatar: string }[];
  yourPredictedSeconds: number;
  courseRecordSeconds: number;
  entryUrl?: string;
  entryStatus?: string;
};

export type LeaderboardEntry = {
  rank: number;
  runner: Pick<Runner, "handle" | "displayName" | "avatar" | "city">;
  timeSeconds: number;
  gapSeconds: number;
  ageGradePct: number;
  raceName: string;
};

export type WallEvent =
  | {
      kind: "pb";
      id: string;
      at: string;
      runner: Pick<Runner, "handle" | "displayName" | "avatar">;
      distanceLabel: string;
      timeSeconds: number;
      previousPbSeconds: number;
      raceName: string;
      /** Permanent shareable BOOM moment URL, if minted. */
      boomId?: string;
    }
  | {
      kind: "prediction";
      id: string;
      at: string;
      runner: Pick<Runner, "handle" | "displayName" | "avatar">;
      raceName: string;
      predictedSeconds: number;
      boomProbabilityPct: number;
    }
  | {
      kind: "rivalry";
      id: string;
      at: string;
      runner: Pick<Runner, "handle" | "displayName" | "avatar">;
      rival: Pick<Runner, "handle" | "displayName" | "avatar">;
      distanceLabel: string;
      runnerSeconds: number;
      rivalSeconds: number;
    };

export const runners: Record<string, Runner> = {
  marc: {
    handle: "marc",
    displayName: "Marc Knowles",
    city: "Cape Town",
    country: "ZA",
    countryFlag: "🇿🇦",
    ageBracket: "M 35–39",
    avatar: "https://i.pravatar.cc/200?img=12",
    form: "rising",
    formDelta: 0.041,
    pbs: [
      { distance: "5K", timeSeconds: 18 * 60 + 42, setAt: "2026-03-02" },
      { distance: "10K", timeSeconds: 39 * 60 + 42, setAt: "2026-04-21" },
      { distance: "Half", timeSeconds: 88 * 60 + 14, setAt: "2026-01-14" },
      { distance: "Marathon", timeSeconds: 3 * 3600 + 9 * 60 + 31, setAt: "2025-09-08" },
    ],
  },
  sipho: {
    handle: "sipho",
    displayName: "Sipho Dlamini",
    city: "Cape Town",
    country: "ZA",
    countryFlag: "🇿🇦",
    ageBracket: "M 35–39",
    avatar: "https://i.pravatar.cc/200?img=33",
    form: "rising",
    formDelta: 0.058,
    pbs: [
      { distance: "5K", timeSeconds: 15 * 60 + 18, setAt: "2026-02-10" },
      { distance: "10K", timeSeconds: 32 * 60 + 18, setAt: "2026-05-09" },
      { distance: "Half", timeSeconds: 71 * 60 + 4, setAt: "2026-01-21" },
      { distance: "Marathon", timeSeconds: 2 * 3600 + 38 * 60 + 12, setAt: "2025-11-02" },
    ],
  },
  lerato: {
    handle: "lerato",
    displayName: "Lerato Mokoena",
    city: "Cape Town",
    country: "ZA",
    countryFlag: "🇿🇦",
    ageBracket: "W 30–34",
    avatar: "https://i.pravatar.cc/200?img=47",
    form: "steady",
    formDelta: 0.008,
    pbs: [
      { distance: "5K", timeSeconds: 19 * 60 + 4, setAt: "2026-03-21" },
      { distance: "10K", timeSeconds: 40 * 60 + 12, setAt: "2026-04-14" },
      { distance: "Half", timeSeconds: 90 * 60 + 28, setAt: "2025-12-03" },
      { distance: "Marathon", timeSeconds: 3 * 3600 + 14 * 60 + 47, setAt: "2025-09-29" },
    ],
  },
  tk: {
    handle: "tk",
    displayName: "TK Nkosi",
    city: "Cape Town",
    country: "ZA",
    countryFlag: "🇿🇦",
    ageBracket: "M 30–34",
    avatar: "https://i.pravatar.cc/200?img=58",
    form: "rising",
    formDelta: 0.032,
    pbs: [
      { distance: "5K", timeSeconds: 16 * 60 + 2, setAt: "2026-03-08" },
      { distance: "10K", timeSeconds: 33 * 60 + 41, setAt: "2026-04-21" },
      { distance: "Half", timeSeconds: 74 * 60 + 12, setAt: "2026-02-04" },
      { distance: "Marathon", timeSeconds: 2 * 3600 + 49 * 60 + 8, setAt: "2025-10-19" },
    ],
  },
};

export const heroRunner: Runner = runners.marc!;

export const heroPrediction: Prediction = {
  raceSlug: "two-oceans-half-2026",
  raceName: "Two Oceans Half Marathon 2026",
  distanceLabel: "Half",
  p10Seconds: 86 * 60 + 40,
  p50Seconds: 88 * 60 + 5,
  p90Seconds: 89 * 60 + 50,
  boomProbabilityPct: 64,
  drivers: [
    { label: "Fitness trend +4%", deltaSeconds: -22 },
    { label: "Course +180m gain", deltaSeconds: +28 },
    { label: "Forecast 18°C / 62% RH", deltaSeconds: +15 },
    { label: "Recent 10k PB carryover", deltaSeconds: -18 },
  ],
};

export const upcomingRaces: RaceEvent[] = [
  {
    slug: "green-point-parkrun-5k",
    name: "Green Point parkrun",
    city: "Cape Town",
    country: "ZA",
    date: "2026-05-24",
    distanceLabel: "5K",
    elevationM: 18,
    terrain: "road",
    friendsGoing: [
      { handle: "sipho", avatar: "https://i.pravatar.cc/64?img=33" },
      { handle: "tk", avatar: "https://i.pravatar.cc/64?img=58" },
    ],
    yourPredictedSeconds: 18 * 60 + 38,
    courseRecordSeconds: 13 * 60 + 58,
    entryUrl: "https://www.parkrun.co.za/greenpoint/",
    entryStatus: "Register",
  },
  {
    slug: "two-oceans-half-2026",
    name: "Two Oceans Half Marathon",
    city: "Cape Town",
    country: "ZA",
    date: "2026-04-04",
    distanceLabel: "Half",
    elevationM: 180,
    terrain: "road",
    friendsGoing: [
      { handle: "sipho", avatar: "https://i.pravatar.cc/64?img=33" },
      { handle: "lerato", avatar: "https://i.pravatar.cc/64?img=47" },
      { handle: "jess", avatar: "https://i.pravatar.cc/64?img=22" },
    ],
    yourPredictedSeconds: 88 * 60 + 5,
    courseRecordSeconds: 60 * 60 + 21,
    entryUrl: "https://www.twooceansmarathon.org.za/",
    entryStatus: "Entries",
  },
  {
    slug: "fnb-cape-town-10k-2026",
    name: "FNB Cape Town 10K",
    city: "Cape Town",
    country: "ZA",
    date: "2026-05-31",
    distanceLabel: "10K",
    elevationM: 42,
    terrain: "road",
    friendsGoing: [
      { handle: "sipho", avatar: "https://i.pravatar.cc/64?img=33" },
      { handle: "tk", avatar: "https://i.pravatar.cc/64?img=58" },
    ],
    yourPredictedSeconds: 39 * 60 + 28,
    courseRecordSeconds: 27 * 60 + 41,
    entryUrl: "https://capetown10k.com/",
    entryStatus: "Entries",
  },
  {
    slug: "knysna-forest-trail-2026",
    name: "Knysna Forest Trail Marathon",
    city: "Knysna",
    country: "ZA",
    date: "2026-07-12",
    distanceLabel: "Marathon",
    elevationM: 840,
    terrain: "trail",
    friendsGoing: [
      { handle: "lerato", avatar: "https://i.pravatar.cc/64?img=47" },
    ],
    yourPredictedSeconds: 3 * 3600 + 18 * 60 + 12,
    courseRecordSeconds: 2 * 3600 + 49 * 60 + 8,
    entryUrl: "https://knysnaforestmarathon.co.za/",
    entryStatus: "Entries",
  },
  {
    slug: "comrades-marathon-2026",
    name: "Comrades Marathon",
    city: "Durban",
    country: "ZA",
    date: "2026-06-14",
    distanceLabel: "Comrades",
    elevationM: 1200,
    terrain: "road",
    friendsGoing: [
      { handle: "sipho", avatar: "https://i.pravatar.cc/64?img=33" },
      { handle: "lerato", avatar: "https://i.pravatar.cc/64?img=47" },
    ],
    yourPredictedSeconds: 8 * 3600 + 12 * 60,
    courseRecordSeconds: 5 * 3600 + 13 * 60 + 58,
    entryUrl: "https://www.comrades.com/",
    entryStatus: "Entries",
  },
];

export type Distance = "5K" | "10K" | "Half" | "Marathon";
export type City = "Cape Town" | "Johannesburg" | "Stellenbosch";

const RAW_LEADERBOARDS: Record<
  Distance,
  Array<Omit<LeaderboardEntry, "rank" | "gapSeconds">>
> = {
  "5K": [
    { runner: { handle: "sipho", displayName: "Sipho Dlamini", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=33" }, timeSeconds: 15 * 60 + 18, ageGradePct: 84.1, raceName: "Cape Town Park Run" },
    { runner: { handle: "tk", displayName: "TK Nkosi", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=58" }, timeSeconds: 16 * 60 + 2, ageGradePct: 80.9, raceName: "Hout Bay 5K" },
    { runner: { handle: "marc", displayName: "Marc Knowles", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=12" }, timeSeconds: 18 * 60 + 42, ageGradePct: 71.5, raceName: "Sea Point Park Run" },
    { runner: { handle: "lerato", displayName: "Lerato Mokoena", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=47" }, timeSeconds: 19 * 60 + 4, ageGradePct: 79.6, raceName: "Constantia 5K" },
    { runner: { handle: "naledi", displayName: "Naledi Mbeki", city: "Johannesburg", avatar: "https://i.pravatar.cc/120?img=20" }, timeSeconds: 17 * 60 + 38, ageGradePct: 82.7, raceName: "Delta Park 5K" },
    { runner: { handle: "wessel", displayName: "Wessel Botha", city: "Stellenbosch", avatar: "https://i.pravatar.cc/120?img=51" }, timeSeconds: 16 * 60 + 51, ageGradePct: 81.2, raceName: "Eikestad 5K" },
    { runner: { handle: "aisha", displayName: "Aisha Patel", city: "Johannesburg", avatar: "https://i.pravatar.cc/120?img=44" }, timeSeconds: 19 * 60 + 22, ageGradePct: 78.3, raceName: "Emmarentia 5K" },
  ],
  "10K": [
    { runner: { handle: "sipho", displayName: "Sipho Dlamini", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=33" }, timeSeconds: 32 * 60 + 18, ageGradePct: 82.4, raceName: "FNB Cape Town 10K 2025" },
    { runner: { handle: "tk", displayName: "TK Nkosi", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=58" }, timeSeconds: 33 * 60 + 41, ageGradePct: 80.1, raceName: "Gun Run 10K 2025" },
    { runner: { handle: "marc", displayName: "Marc Knowles", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=12" }, timeSeconds: 39 * 60 + 42, ageGradePct: 71.3, raceName: "Old Mutual Two Oceans 10K 2026" },
    { runner: { handle: "lerato", displayName: "Lerato Mokoena", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=47" }, timeSeconds: 40 * 60 + 12, ageGradePct: 78.9, raceName: "Spar Women's 10K 2025" },
    { runner: { handle: "naledi", displayName: "Naledi Mbeki", city: "Johannesburg", avatar: "https://i.pravatar.cc/120?img=20" }, timeSeconds: 36 * 60 + 14, ageGradePct: 81.8, raceName: "Sasol 10K" },
    { runner: { handle: "wessel", displayName: "Wessel Botha", city: "Stellenbosch", avatar: "https://i.pravatar.cc/120?img=51" }, timeSeconds: 34 * 60 + 51, ageGradePct: 80.7, raceName: "Stellenbosch 10K 2026" },
    { runner: { handle: "aisha", displayName: "Aisha Patel", city: "Johannesburg", avatar: "https://i.pravatar.cc/120?img=44" }, timeSeconds: 41 * 60 + 22, ageGradePct: 77.8, raceName: "Discovery 10K" },
  ],
  Half: [
    { runner: { handle: "sipho", displayName: "Sipho Dlamini", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=33" }, timeSeconds: 71 * 60 + 4, ageGradePct: 83.6, raceName: "Two Oceans Half 2025" },
    { runner: { handle: "tk", displayName: "TK Nkosi", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=58" }, timeSeconds: 74 * 60 + 12, ageGradePct: 80.5, raceName: "Knysna Half 2025" },
    { runner: { handle: "marc", displayName: "Marc Knowles", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=12" }, timeSeconds: 88 * 60 + 14, ageGradePct: 72.1, raceName: "Two Oceans Half 2026" },
    { runner: { handle: "lerato", displayName: "Lerato Mokoena", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=47" }, timeSeconds: 90 * 60 + 28, ageGradePct: 79.4, raceName: "Knysna Half 2025" },
    { runner: { handle: "naledi", displayName: "Naledi Mbeki", city: "Johannesburg", avatar: "https://i.pravatar.cc/120?img=20" }, timeSeconds: 81 * 60 + 12, ageGradePct: 81.9, raceName: "Joburg City Half" },
    { runner: { handle: "wessel", displayName: "Wessel Botha", city: "Stellenbosch", avatar: "https://i.pravatar.cc/120?img=51" }, timeSeconds: 76 * 60 + 8, ageGradePct: 80.0, raceName: "Helderberg Half" },
  ],
  Marathon: [
    { runner: { handle: "sipho", displayName: "Sipho Dlamini", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=33" }, timeSeconds: 2 * 3600 + 38 * 60 + 12, ageGradePct: 82.9, raceName: "Cape Town Marathon 2025" },
    { runner: { handle: "tk", displayName: "TK Nkosi", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=58" }, timeSeconds: 2 * 3600 + 49 * 60 + 8, ageGradePct: 80.2, raceName: "Comrades 2025" },
    { runner: { handle: "marc", displayName: "Marc Knowles", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=12" }, timeSeconds: 3 * 3600 + 9 * 60 + 31, ageGradePct: 73.8, raceName: "Cape Town Marathon 2025" },
    { runner: { handle: "lerato", displayName: "Lerato Mokoena", city: "Cape Town", avatar: "https://i.pravatar.cc/120?img=47" }, timeSeconds: 3 * 3600 + 14 * 60 + 47, ageGradePct: 79.1, raceName: "Cape Town Marathon 2025" },
    { runner: { handle: "naledi", displayName: "Naledi Mbeki", city: "Johannesburg", avatar: "https://i.pravatar.cc/120?img=20" }, timeSeconds: 2 * 3600 + 51 * 60 + 4, ageGradePct: 81.7, raceName: "Sanlam Joburg Marathon" },
    { runner: { handle: "wessel", displayName: "Wessel Botha", city: "Stellenbosch", avatar: "https://i.pravatar.cc/120?img=51" }, timeSeconds: 2 * 3600 + 44 * 60 + 21, ageGradePct: 80.4, raceName: "Cape Town Marathon 2025" },
  ],
};

function rank(entries: Array<Omit<LeaderboardEntry, "rank" | "gapSeconds">>): LeaderboardEntry[] {
  const sorted = [...entries].sort((a, b) => a.timeSeconds - b.timeSeconds);
  const lead = sorted[0]?.timeSeconds ?? 0;
  return sorted.map((entry, i) => ({
    ...entry,
    rank: i + 1,
    gapSeconds: entry.timeSeconds - lead,
  }));
}

export function getLeaderboard({
  distance,
  city,
}: {
  distance: Distance;
  city: City | "all";
}): LeaderboardEntry[] {
  const all = RAW_LEADERBOARDS[distance];
  const filtered = city === "all" ? all : all.filter((e) => e.runner.city === city);
  return rank(filtered);
}

export const CITIES: Array<City | "all"> = ["all", "Cape Town", "Johannesburg", "Stellenbosch"];
export const DISTANCES: Distance[] = ["5K", "10K", "Half", "Marathon"];

// Legacy default for the home page (Cape Town 10K).
export const leaderboard: LeaderboardEntry[] = getLeaderboard({
  distance: "10K",
  city: "Cape Town",
});

export type WeeklyMover = {
  runner: Pick<Runner, "handle" | "displayName" | "avatar" | "city">;
  distanceLabel: Distance;
  newTimeSeconds: number;
  previousTimeSeconds: number;
  raceName: string;
};

export const weeklyMovers: WeeklyMover[] = [
  {
    runner: { handle: "sipho", displayName: "Sipho Dlamini", avatar: "https://i.pravatar.cc/120?img=33", city: "Cape Town" },
    distanceLabel: "10K",
    newTimeSeconds: 32 * 60 + 18,
    previousTimeSeconds: 33 * 60 + 36,
    raceName: "FNB Cape Town 10K",
  },
  {
    runner: { handle: "wessel", displayName: "Wessel Botha", avatar: "https://i.pravatar.cc/120?img=51", city: "Stellenbosch" },
    distanceLabel: "Half",
    newTimeSeconds: 76 * 60 + 8,
    previousTimeSeconds: 78 * 60 + 41,
    raceName: "Helderberg Half",
  },
  {
    runner: { handle: "naledi", displayName: "Naledi Mbeki", avatar: "https://i.pravatar.cc/120?img=20", city: "Johannesburg" },
    distanceLabel: "5K",
    newTimeSeconds: 17 * 60 + 38,
    previousTimeSeconds: 18 * 60 + 14,
    raceName: "Delta Park 5K",
  },
];

export const wall: WallEvent[] = [
  {
    kind: "pb",
    id: "w1",
    at: "2026-05-10T07:18:00Z",
    runner: { handle: "sipho", displayName: "Sipho Dlamini", avatar: "https://i.pravatar.cc/120?img=33" },
    distanceLabel: "10K",
    timeSeconds: 32 * 60 + 18,
    previousPbSeconds: 33 * 60 + 36,
    raceName: "FNB Cape Town 10K",
    boomId: "sipho-10k-fnb-2025",
  },
  {
    kind: "prediction",
    id: "w2",
    at: "2026-05-10T06:02:00Z",
    runner: { handle: "marc", displayName: "Marc Knowles", avatar: "https://i.pravatar.cc/120?img=12" },
    raceName: "Two Oceans Half 2026",
    predictedSeconds: 88 * 60 + 5,
    boomProbabilityPct: 64,
  },
  {
    kind: "rivalry",
    id: "w3",
    at: "2026-05-09T16:44:00Z",
    runner: { handle: "marc", displayName: "Marc Knowles", avatar: "https://i.pravatar.cc/120?img=12" },
    rival: { handle: "sipho", displayName: "Sipho Dlamini", avatar: "https://i.pravatar.cc/120?img=33" },
    distanceLabel: "5K",
    runnerSeconds: 18 * 60 + 42,
    rivalSeconds: 17 * 60 + 51,
  },
];

/** Permanent shareable BOOM moments. The URL is the artifact a runner posts. */
export type BoomMoment = {
  id: string;
  runnerHandle: string;
  runnerName: string;
  distanceLabel: Distance;
  newTimeSeconds: number;
  previousPbSeconds: number | null;
  raceName: string;
  setAt: string;
};

export const boomMoments: Record<string, BoomMoment> = {
  "marc-10k-fnb-2026": {
    id: "marc-10k-fnb-2026",
    runnerHandle: "marc",
    runnerName: "Marc Knowles",
    distanceLabel: "10K",
    newTimeSeconds: 39 * 60 + 42,
    previousPbSeconds: 41 * 60 + 0,
    raceName: "FNB Cape Town 10K 2026",
    setAt: "2026-04-21T07:18:00Z",
  },
  "sipho-10k-fnb-2025": {
    id: "sipho-10k-fnb-2025",
    runnerHandle: "sipho",
    runnerName: "Sipho Dlamini",
    distanceLabel: "10K",
    newTimeSeconds: 32 * 60 + 18,
    previousPbSeconds: 33 * 60 + 36,
    raceName: "FNB Cape Town 10K 2025",
    setAt: "2025-09-08T07:18:00Z",
  },
  "marc-half-two-oceans-2026": {
    id: "marc-half-two-oceans-2026",
    runnerHandle: "marc",
    runnerName: "Marc Knowles",
    distanceLabel: "Half",
    newTimeSeconds: 88 * 60 + 14,
    previousPbSeconds: 90 * 60 + 28,
    raceName: "Two Oceans Half 2026",
    setAt: "2026-01-14T05:30:00Z",
  },
};
